// The web build's Training Portal sign-in gateway, moved here from hogc-one-frontend (#414).
// The Thinkific theme sends signed-out visitors to `/training?fullpath=<path on Thinkific>`, and
// its sign-out to `/training/signed-out`.

export const TRAINING_GATEWAY_PATH = '/training';
export const TRAINING_SIGNED_OUT_PATH = '/training/signed-out';
export const TRAINING_GATEWAY_PATHS = Object.freeze([
  TRAINING_GATEWAY_PATH,
  TRAINING_SIGNED_OUT_PATH,
]);

// Where every way out of the gateway goes: the church website, never the app (its desktop view
// is not ready for people arriving from the Training Portal).
export const HOGC_WEBSITE_URL = 'https://heartofgodchurch.org';

// Survives the Auth0 round trip in this tab only. See `trainingCallbackRedirect`.
export const TRAINING_RETURN_KEY = 'hogc_training_return';
const TRAINING_RETURN_MAX_AGE_MS = 15 * 60 * 1000;

/**
 * The Thinkific path to return to, or null when it is missing or unsafe. Only a site-relative
 * path is kept: one leading "/", not "//" or "/\" (both read as another host), no control chars.
 * @param {unknown} value a route query value (string, or an array when repeated)
 * @returns {string|null}
 */
export function sanitizeFullPath(value) {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string' || !raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F\u007F]/.test(raw)) return null;
  return raw;
}

/** The gateway route to come back to after sign-in, fullpath kept. */
export function trainingReturnRoute(fullPath) {
  return fullPath
    ? { path: TRAINING_GATEWAY_PATH, query: { fullpath: fullPath } }
    : { path: TRAINING_GATEWAY_PATH, query: {} };
}

/** The same route as a string, for Auth0's `appState.target`. */
export function trainingReturnTarget(fullPath) {
  return fullPath
    ? `${TRAINING_GATEWAY_PATH}?fullpath=${encodeURIComponent(fullPath)}`
    : TRAINING_GATEWAY_PATH;
}

function storage() {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Remember, for this tab, that the next Auth0 callback belongs to the gateway. */
export function saveTrainingReturn(fullPath, now = Date.now()) {
  try {
    storage()?.setItem(
      TRAINING_RETURN_KEY,
      JSON.stringify({ fullPath: fullPath || null, at: now }),
    );
  } catch (error) {
    console.warn('[Training] Could not store the return path:', error?.message);
  }
}

/** The pending return, if one was saved in the last 15 minutes. Does not consume it. */
export function readTrainingReturn(now = Date.now()) {
  try {
    const parsed = JSON.parse(storage()?.getItem(TRAINING_RETURN_KEY) || 'null');
    if (!parsed || typeof parsed.at !== 'number') return null;
    if (now - parsed.at > TRAINING_RETURN_MAX_AGE_MS || now < parsed.at) return null;
    return { fullPath: sanitizeFullPath(parsed.fullPath) };
  } catch {
    return null;
  }
}

export function clearTrainingReturn() {
  try {
    storage()?.removeItem(TRAINING_RETURN_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * The gateway route a pending Auth0 sign-in will return to, read from auth0-spa-js's own
 * transaction (`a0.spajs.txs.<clientId>` in sessionStorage, where loginWithRedirect keeps
 * `appState`). Only `appState.target` is read. Null unless it targets `/training`.
 */
export function auth0TransactionTrainingRoute(env = process.env) {
  try {
    const raw = storage()?.getItem(`a0.spajs.txs.${env.VUE_APP_AUTH0_CLIENT_ID}`);
    const target = raw ? JSON.parse(raw)?.appState?.target : null;
    if (typeof target !== 'string') return null;
    const url = new URL(target, 'https://gateway.invalid');
    if (url.origin !== 'https://gateway.invalid' || url.pathname !== TRAINING_GATEWAY_PATH) {
      return null;
    }
    return trainingReturnRoute(sanitizeFullPath(url.searchParams.get('fullpath')));
  } catch {
    return null;
  }
}

/**
 * Where `/` should send an Auth0 callback the gateway started, or null for every other visit.
 *
 * On the web build Auth0 returns to the origin (`createRedirectUri`), and `/` redirects to
 * `/login`, whose post-login flow runs the onboarding and home redirects. A gateway sign-in has
 * to skip all of that, so a callback (`code`/`error` + `state` in the query) on `/` goes straight
 * back to `/training` when either the gateway's own return is pending or, failing that, Auth0's
 * transaction says its `appState.target` is `/training`. auth0-vue reads the callback from
 * `window.location` when the plugin installs, before this navigation changes the URL, then
 * pushes `appState.target`, which is the same route.
 *
 * @param {{ query?: Record<string, unknown> }} to
 * @returns {{ path: string, query: object }|null}
 */
export function trainingCallbackRedirect(to, now = Date.now(), env = process.env) {
  const query = to?.query || {};
  const isCallback = Boolean(query.state) && Boolean(query.code || query.error);
  if (!isCallback) return null;
  const pending = readTrainingReturn(now);
  const route = pending
    ? trainingReturnRoute(pending.fullPath)
    : auth0TransactionTrainingRoute(env);
  if (route) callbackRouted = true;
  return route;
}

// Set when `/` routed an Auth0 callback here, for this page load only.
let callbackRouted = false;

/**
 * Whether this page load is the Auth0 callback of a gateway sign-in (cleared once read), so the
 * gateway can tell "came back still signed out" from "never asked" and not start another sign-in.
 */
export function takeTrainingCallbackRouted() {
  const routed = callbackRouted;
  callbackRouted = false;
  return routed;
}

/**
 * Router guard: once on a gateway route, stay on gateway routes. The only ways out are full-page
 * redirects (Thinkific, Auth0, the church website). This stops anything in the app taking the
 * visitor into it: auth0-vue's error push to `/` (then `/login`), a login page timer, a boot
 * redirect.
 * @returns {boolean|undefined} false cancels the navigation
 */
export function keepOnTrainingGateway(to, from) {
  const onGateway = TRAINING_GATEWAY_PATHS.includes(from?.path);
  if (onGateway && !TRAINING_GATEWAY_PATHS.includes(to?.path)) return false;
  return undefined;
}
